import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, UserCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LivingBackground } from "../components/LivingBackground";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate(user.role === "client" ? "/portal" : "/mail");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé");
      navigate(res.user?.role === "client" ? "/portal" : "/mail");
    } else {
      setError(res.error);
    }
  };

  const [clientLoading, setClientLoading] = useState(false);
  const clientQuickLogin = async () => {
    setError("");
    setClientLoading(true);
    const res = await login("client@flowdesk.app", "password123");
    setClientLoading(false);
    if (res.ok) { toast.success("Bienvenue dans votre Espace client"); navigate("/portal"); }
    else setError("Compte démo client indisponible");
  };

  const fill = (email) => setForm({ ...form, email, password: email === "admin@flowdesk.app" ? "admin123" : "password123" });

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPreview = host.includes("preview.emergentagent.com") || host === "localhost" || host === "127.0.0.1";

  return (
    <div className="relative min-h-[100dvh] flex bg-transparent">
      <LivingBackground />
      {/* Hero */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden" style={{ background: "#08080a" }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-1/4 -left-1/4 w-[80%] h-[80%] rounded-full blur-[130px]" style={{ background: "radial-gradient(circle, rgba(79,156,255,0.38), transparent 60%)" }} />
          <div className="absolute bottom-[-25%] right-[-12%] w-[72%] h-[72%] rounded-full blur-[130px]" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.32), transparent 60%)" }} />
          <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(79,156,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(79,156,255,0.045) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(ellipse at 50% 35%, #000 0%, transparent 78%)", maskImage: "radial-gradient(ellipse at 50% 35%, #000 0%, transparent 78%)" }} />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3 self-start">
            <img src="/brand/flowdesk-mark.png" alt="FlowDesk" className="h-11 w-auto object-contain drop-shadow-[0_2px_22px_rgba(79,156,255,0.4)]" />
            <span className="font-cabinet font-black text-2xl tracking-tight">FlowDesk</span>
          </div>
          <div className="animate-slide-up">
            <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-klein/80 mb-4">Email · Tâches · Chat</p>
            <h1 className="font-cabinet font-black text-5xl xl:text-6xl leading-[0.95] tracking-tighter mb-6">
              Transformez chaque message en <span className="bg-gradient-to-r from-[#4F9CFF] to-[#2563EB] bg-clip-text text-transparent">action.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-md">
              L'hybride entre messagerie et gestion de tâches. Convertissez un email en ticket, suivez-le en Kanban, et faites valider vos livraisons par le client.
            </p>
          </div>
          <div className="font-mono-label text-xs text-white/40 uppercase tracking-widest">© 2026 FlowDesk</div>
        </div>
      </div>

      {/* Form */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-in">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/brand/flowdesk-mark.png" alt="" className="w-9 h-9 object-contain" />
            <span className="font-cabinet font-black text-2xl">FlowDesk</span>
          </div>

          <h2 className="font-cabinet font-bold text-3xl tracking-tight mb-1">
            {mode === "login" ? "Bon retour." : "Créer un compte"}
          </h2>
          <p className="text-zinc-500 mb-8">
            {mode === "login" ? "Connectez-vous pour accéder à votre espace." : "Quelques secondes suffisent."}
          </p>

          <div className="flex gap-1 p-1 bg-white/[0.06] rounded-md mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2 rounded-sm text-sm font-medium transition-colors ${mode === m ? "bg-card shadow-sm text-zinc-100" : "text-zinc-500"}`}
              >
                {m === "login" ? "Connexion" : "Inscription"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field icon={User} placeholder="Nom complet" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="name-input" />
            )}
            <Field icon={Mail} type="email" placeholder="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="email-input" />
            <Field icon={Lock} type="password" placeholder="Mot de passe" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="password-input" />

            {error && <p data-testid="auth-error" className="text-sm text-signal">{error}</p>}

            <button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-klein text-zinc-950 py-3 rounded-md font-semibold hover:-translate-y-px hover:shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center"><span className="bg-background px-3 text-xs text-zinc-400 font-mono-label uppercase tracking-widest">ou</span></div>
          </div>

          <button
            type="button"
            data-testid="google-login-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/google-callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 bg-card border border-white/10 py-3 rounded-md font-medium hover:bg-background transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Se connecter avec Google
          </button>

          <button
            type="button"
            data-testid="client-quick-login"
            onClick={clientQuickLogin}
            disabled={clientLoading}
            className={`mt-3 w-full flex items-center justify-center gap-2.5 py-3 rounded-md font-semibold text-white bg-gradient-to-r from-[#4F9CFF] to-[#2563EB] hover:brightness-110 transition-all disabled:opacity-60 ${isPreview ? "" : "hidden"}`}
          >
            <UserCircle size={18} />
            {clientLoading ? "Connexion…" : "Connexion rapide — Espace client (démo)"}
          </button>

          {isPreview && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400 mb-3">Comptes de démonstration</p>
            <div className="grid grid-cols-1 gap-2">
              {[
                { e: "admin@flowdesk.app", n: "Admin (vous)" },
                { e: "marie@flowdesk.app", n: "Marie (collègue)" },
                { e: "client@flowdesk.app", n: "Client Martin" },
              ].map((d) => (
                <button
                  key={d.e}
                  type="button"
                  data-testid={`demo-${d.e}`}
                  onClick={() => { setMode("login"); fill(d.e); }}
                  className="text-left px-3 py-2 rounded-md border border-white/10 hover:border-klein transition-colors text-sm flex justify-between items-center"
                >
                  <span className="font-medium">{d.n}</span>
                  <span className="font-mono-label text-xs text-zinc-400">{d.e}</span>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, type = "text", placeholder, value, onChange, testid }) {
  return (
    <div className="relative">
      <Icon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
      <input
        data-testid={testid}
        type={type}
        required
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-card border border-white/10 rounded-md pl-11 pr-4 py-3 text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein transition-all"
      />
    </div>
  );
}
